// 7 Learning Center pages — educational content with soft NordVPN CTAs
export const LEARN = [
  {
    slug: "what-is-a-vpn",
    label: "What Is a VPN?",
    title: "What Is a VPN?",
    metaTitle: "What Is a VPN? Virtual Private Networks Explained Simply",
    metaDescription:
      "A plain-English explanation of what a VPN is, how it protects you, and when you should use one. No jargon, just clarity.",
    intro:
      "A VPN (Virtual Private Network) creates an encrypted tunnel between your device and the internet, hiding your activity from your ISP, network operators, and snoopers — and making you appear to be in a different location.",
    sections: [
      {
        h: "The Problem a VPN Solves",
        p: [
          "Every time you go online without protection, your internet provider can see every website you visit. Public Wi-Fi operators can potentially intercept your traffic. And websites see your real IP address, which reveals your approximate location.",
          "A VPN solves all three problems at once: your ISP sees only an encrypted connection to a VPN server, Wi-Fi snoopers see scrambled data, and websites see the VPN server's IP instead of yours.",
        ],
      },
      {
        h: "How the Encrypted Tunnel Works",
        p: [
          "When you connect to a VPN, your device and the VPN server perform a cryptographic handshake and agree on encryption keys. From that moment, everything you send is encrypted with AES-256 (or ChaCha20) before it leaves your device — mathematically unreadable to anyone in between.",
          "The VPN server decrypts your traffic and forwards it to its destination, then returns the response through the same encrypted tunnel.",
        ],
      },
      {
        h: "What a VPN Does and Doesn't Do",
        list: [
          "Does: hide browsing from your ISP, secure public Wi-Fi, change your apparent location, prevent targeted throttling",
          "Doesn't: make you fully anonymous, protect against malware, hide activity from sites you're logged into",
        ],
      },
      {
        h: "Getting Started",
        p: [
          "Choose an audited, no-log provider, install the app, and press connect — that's genuinely all there is to it. Our top pick is NordVPN: fastest speeds, verified no-log policy, and apps for every platform, from $3.09/month.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "How VPNs Work", to: "/learn/how-vpns-work" },
      { label: "Best VPNs of 2026", to: "/best-vpns" },
    ],
  },
  {
    slug: "how-vpns-work",
    label: "How VPNs Work",
    title: "How VPNs Work: The Technology Explained",
    metaTitle: "How VPNs Work: Encryption, Tunneling & Servers Explained",
    metaDescription:
      "Understand the technology behind VPNs — tunneling, encryption, handshakes, and servers — explained clearly for non-engineers.",
    intro:
      "Under the hood, a VPN combines three technologies: tunneling protocols, encryption ciphers, and a global server network. Here's how they fit together.",
    sections: [
      {
        h: "Step 1: The Handshake",
        p: [
          "When you press connect, your device authenticates with the VPN server and both sides negotiate encryption keys using public-key cryptography. This handshake takes milliseconds on modern protocols like WireGuard.",
        ],
      },
      {
        h: "Step 2: Tunneling",
        p: [
          "Your data packets are encrypted and wrapped inside outer packets addressed to the VPN server — a process called encapsulation. To your ISP, the traffic is an opaque stream to a single server. The actual destinations of your traffic are hidden inside the encrypted payload.",
        ],
      },
      {
        h: "Step 3: The Exit Server",
        p: [
          "The VPN server decrypts your packets and sends them to their real destinations using its own IP address. Responses flow back through the server and down the tunnel to you. This is why websites see the server's location, not yours — and why server network size matters when choosing a provider.",
        ],
      },
      {
        h: "Why Protocol Choice Matters",
        p: [
          "The protocol determines how fast and robust the tunnel is. WireGuard (and NordVPN's NordLynx implementation) offers the best speed-to-security ratio today; OpenVPN remains the most battle-tested fallback. See our protocol guide for details.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "VPN Protocols Explained", to: "/learn/vpn-protocols-explained" },
      { label: "WireGuard vs OpenVPN", to: "/learn/wireguard-vs-openvpn" },
    ],
  },
  {
    slug: "vpn-protocols-explained",
    label: "VPN Protocols",
    title: "VPN Protocols Explained",
    metaTitle: "VPN Protocols Explained: WireGuard, OpenVPN, IKEv2 & More",
    metaDescription:
      "WireGuard, OpenVPN, IKEv2, NordLynx — what's the difference, and which should you use? Every major VPN protocol explained.",
    intro:
      "A VPN protocol is the rulebook that governs how your encrypted tunnel is built and maintained. Your protocol choice affects speed, stability, and censorship resistance.",
    sections: [
      {
        h: "WireGuard",
        p: [
          "The modern standard. Around 4,000 lines of code, state-of-the-art ChaCha20 cryptography, and typically 2-3x faster than OpenVPN. Reconnects instantly after network changes, which makes it ideal for mobile. NordVPN's NordLynx is WireGuard with a double-NAT privacy layer on top.",
        ],
      },
      {
        h: "OpenVPN",
        p: [
          "The battle-tested veteran, in service since 2001. Open-source, audited endlessly, and extremely configurable. Runs over UDP (faster) or TCP (more reliable, and on port 443 it blends in with HTTPS traffic — useful against censorship). Slower than WireGuard due to older design.",
        ],
      },
      {
        h: "IKEv2/IPsec",
        p: [
          "A solid mobile protocol known for fast reconnection when switching between Wi-Fi and cellular. Largely superseded by WireGuard but still a dependable option, particularly on iOS.",
        ],
      },
      {
        h: "Proprietary Protocols",
        p: [
          "Some providers build their own: NordLynx (NordVPN, WireGuard-based) and AdGuard VPN's HTTPS-disguise protocol are examples. These typically optimise for speed or stealth respectively.",
        ],
      },
      {
        h: "Which Should You Use?",
        p: [
          "Use WireGuard/NordLynx by default for speed. Switch to OpenVPN TCP when you need to bypass strict firewalls or censorship. IKEv2 is a fine fallback on Apple devices.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "WireGuard vs OpenVPN", to: "/learn/wireguard-vs-openvpn" },
      { label: "NordVPN Review", to: "/reviews/nordvpn" },
    ],
  },
  {
    slug: "wireguard-vs-openvpn",
    label: "WireGuard vs OpenVPN",
    title: "WireGuard vs OpenVPN",
    metaTitle: "WireGuard vs OpenVPN: Speed, Security & When to Use Each",
    metaDescription:
      "WireGuard is faster; OpenVPN is more established. We compare speed, security, and censorship resistance to help you pick the right protocol.",
    intro:
      "The two protocols you'll actually choose between in 2026. WireGuard wins on speed and efficiency; OpenVPN wins on configurability and censorship resistance.",
    sections: [
      {
        h: "Speed",
        p: [
          "In our NordVPN tests, NordLynx (WireGuard) retained 95.3% of base speed on UK servers versus 78.4% for OpenVPN on the same servers. WireGuard's lean codebase and modern cryptography deliver a real, measurable advantage — especially on mobile and long-distance connections.",
        ],
      },
      {
        h: "Security",
        p: [
          "Both are secure. OpenVPN's AES-256 has never been cracked; WireGuard's ChaCha20 is equally strong and more efficient on modern CPUs. WireGuard's tiny attack surface (4,000 lines vs 400,000+) is a structural security advantage.",
          "WireGuard's original design stored user IPs on the server — providers like NordVPN solved this with a double-NAT layer (NordLynx).",
        ],
      },
      {
        h: "Censorship Resistance",
        p: [
          "OpenVPN over TCP port 443 disguises itself as ordinary HTTPS traffic, making it harder to block in restrictive countries. WireGuard's UDP traffic is easier for deep packet inspection to identify.",
        ],
      },
      {
        h: "The Verdict",
        p: [
          "Use WireGuard for everyday browsing, streaming, and gaming. Keep OpenVPN TCP in your back pocket for hostile networks. On NordVPN, NordLynx is the default — and the right choice for most people.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "VPN Protocols Explained", to: "/learn/vpn-protocols-explained" },
      { label: "Fastest VPN Rankings", to: "/rankings/fastest-vpn" },
    ],
  },
  {
    slug: "kill-switch-explained",
    label: "Kill Switch",
    title: "VPN Kill Switch Explained",
    metaTitle: "VPN Kill Switch Explained: What It Is & Why You Need One",
    metaDescription:
      "A kill switch cuts your internet if the VPN drops, preventing accidental exposure. Here's how it works and how to enable it.",
    intro:
      "A kill switch is your VPN's safety net: if the encrypted tunnel drops for any reason, it instantly blocks all internet traffic so your real IP and unencrypted data are never exposed.",
    sections: [
      {
        h: "Why VPN Connections Drop",
        p: [
          "Wi-Fi handoffs, sleeping laptops, congested servers, flaky mobile networks — even the best VPN occasionally loses connection for a second or two. Without a kill switch, your device silently falls back to your normal, unencrypted connection during that gap.",
        ],
      },
      {
        h: "How a Kill Switch Works",
        p: [
          "The VPN app monitors the tunnel continuously. The moment it detects a drop, it applies firewall rules that block all outbound traffic until the tunnel is re-established. Good implementations act in milliseconds.",
        ],
        list: [
          "System-level kill switch: blocks all traffic device-wide (strongest)",
          "App-level kill switch: closes only specified apps, e.g. a torrent client",
        ],
      },
      {
        h: "Who Needs One?",
        p: [
          "Everyone should leave it on, but it's essential for torrenting, journalism, or any activity where a single leaked packet matters. All five VPNs we recommend include a kill switch; NordVPN's passed every forced-drop test we ran without leaking a single packet.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "Best Privacy VPN Rankings", to: "/rankings/best-privacy-vpn" },
      { label: "Best VPN for Torrenting", to: "/best-vpn-for/torrenting" },
    ],
  },
  {
    slug: "split-tunneling-explained",
    label: "Split Tunneling",
    title: "Split Tunneling Explained",
    metaTitle: "VPN Split Tunneling Explained: Route Only What You Need",
    metaDescription:
      "Split tunneling lets you choose which apps use the VPN and which use your normal connection. Here's how it works and when to use it.",
    intro:
      "Split tunneling lets you route some traffic through the VPN while the rest uses your regular connection — the best of both worlds when you need speed and protection simultaneously.",
    sections: [
      {
        h: "How It Works",
        p: [
          "Instead of sending 100% of your traffic through the encrypted tunnel, the VPN app applies routing rules per app or per website. Your torrent client can be fully protected while your banking app — which may block VPN IPs — uses your normal connection.",
        ],
      },
      {
        h: "Common Use Cases",
        list: [
          "Stream foreign Netflix in one app while local services work normally in another",
          "Protect torrenting traffic while gaming at full native speed",
          "Access region-locked sites while printers and smart-home devices stay on the local network",
          "Keep banking apps (which often flag VPN IPs) on your regular connection",
        ],
      },
      {
        h: "Inverse Split Tunneling",
        p: [
          "Most apps also support the inverse mode: everything goes through the VPN except the apps you exclude. This is the safer default — protection is opt-out rather than opt-in.",
        ],
      },
      {
        h: "Which VPNs Support It?",
        p: [
          "All five of our recommended VPNs offer split tunneling. NordVPN and Surfshark (Bypasser) have the most polished per-app implementations on Windows and Android.",
        ],
        cta: true,
      },
    ],
    related: [
      { label: "Best VPN for Gaming", to: "/best-vpn-for/gaming" },
      { label: "Best VPNs of 2026", to: "/best-vpns" },
    ],
  },
  {
    slug: "vpn-glossary",
    label: "VPN Glossary",
    title: "VPN Glossary: Every Term Explained",
    metaTitle: "VPN Glossary 2026: Every VPN Term Explained in Plain English",
    metaDescription:
      "AES-256, kill switch, five eyes, RAM-only servers, obfuscation — every VPN term you'll encounter, defined in plain English.",
    intro:
      "The VPN industry loves jargon. This glossary defines every term you're likely to encounter, in plain English.",
    glossary: [
      { term: "AES-256", def: "The industry-standard encryption cipher. 256-bit keys make brute-forcing practically impossible — the same standard used by governments and banks." },
      { term: "ChaCha20", def: "A modern encryption cipher used by WireGuard. As secure as AES-256 and faster on mobile processors." },
      { term: "DNS Leak", def: "When your DNS queries (the 'address lookups' of the web) escape the VPN tunnel and reach your ISP, revealing which sites you visit. Good VPNs route DNS inside the tunnel." },
      { term: "Double VPN / MultiHop", def: "Routing traffic through two VPN servers in different countries for an extra layer of anonymity, at some speed cost." },
      { term: "Five / Nine / Fourteen Eyes", def: "Intelligence-sharing alliances between countries. Privacy purists prefer VPNs headquartered outside these jurisdictions (e.g., NordVPN in Panama)." },
      { term: "Geo-blocking", def: "Restricting content by location, as streaming services do. VPNs bypass it by making you appear to be in another country." },
      { term: "IP Address", def: "The unique number identifying your device on the internet, which reveals your approximate location. A VPN masks it with the server's IP." },
      { term: "Kill Switch", def: "A safety feature that blocks all internet traffic if the VPN connection drops, preventing accidental exposure." },
      { term: "No-Log Policy", def: "A provider's promise not to record your activity. Only meaningful when verified by an independent audit (PwC, Deloitte, KPMG)." },
      { term: "Obfuscation / Stealth", def: "Techniques that disguise VPN traffic as ordinary HTTPS, defeating VPN blocks and deep packet inspection." },
      { term: "OpenVPN", def: "The veteran open-source VPN protocol — extremely secure and configurable, slower than WireGuard." },
      { term: "P2P / Torrenting", def: "Peer-to-peer file sharing. Requires a VPN with P2P-friendly servers, a kill switch, and a verified no-log policy." },
      { term: "RAM-Only Servers", def: "Servers that run entirely in memory, wiping all data on every reboot. Nothing can be seized from disk because nothing is stored on disk." },
      { term: "Split Tunneling", def: "Choosing which apps or sites use the VPN and which use your normal connection." },
      { term: "Threat Protection", def: "NordVPN's network-level blocker for ads, trackers, and malware — works even when the VPN is disconnected." },
      { term: "WireGuard", def: "The modern, lean VPN protocol — typically 2-3x faster than OpenVPN with equally strong cryptography." },
    ],
    sections: [],
    related: [
      { label: "What Is a VPN?", to: "/learn/what-is-a-vpn" },
      { label: "Best VPNs of 2026", to: "/best-vpns" },
    ],
  },
];

export const getLearn = (slug) => LEARN.find((l) => l.slug === slug);
